import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { OutputData } from '@editorjs/editorjs';
import { useMatterStore } from './matterStore';
import { captureEvent } from '../analytics';

/**
 * Narratives for a matter — titled documents assembled from findings, each
 * holding a tree of pages. Backed by the narratives API
 * (routers/narratives_router.py → services/narratives), persisted in the
 * per-matter `narratives` / `narrative_pages` tables.
 *
 * Endpoints (all matter-scoped):
 *   GET    /api/Matter/{id}/Narratives                    — list (summaries)
 *   POST   /api/Matter/{id}/Narrative                     — create (+ first page)
 *   GET    /api/Matter/{id}/Narrative/{nid}               — fetch one (+ pages)
 *   PATCH  /api/Matter/{id}/Narrative/{nid}               — rename
 *   DELETE /api/Matter/{id}/Narrative/{nid}               — delete
 *   POST   /api/Matter/{id}/Narrative/{nid}/Page          — add page
 *   PATCH  /api/Matter/{id}/Narrative/{nid}/Page/{pid}    — rename / save content
 *   DELETE /api/Matter/{id}/Narrative/{nid}/Page/{pid}    — delete page
 */

/** A page within a narrative. `content` is the opaque Editor.js block model. */
export interface NarrativePage {
    id: string;
    title: string;
    parent_id: string | null;
    content: OutputData | null;
    created_on: string;
    updated_on: string;
    /**
     * Findings this page cites — a projection of `content`, recomputed by the
     * backend on every content save (it scans the Editor.js blob for finding
     * anchors). Read-only from the client's perspective.
     */
    cited_finding_ids: string[];
}

/** List-shape narrative (no page content), from GET /Narratives. */
export interface NarrativeSummary {
    id: string;
    title: string;
    created_on: string;
    updated_on: string;
    page_count: number;
    /** Distinct findings cited anywhere in the narrative (rollup across pages). */
    cited_finding_ids: string[];
}

/** Full narrative with its pages, from GET /Narrative/{id}. */
export interface Narrative {
    id: string;
    title: string;
    created_on: string;
    updated_on: string;
    pages: NarrativePage[];
    /** Distinct findings cited anywhere in the narrative (rollup across pages). */
    cited_finding_ids: string[];
}

export const useNarrativesStore = defineStore('narratives', () => {
    // List (sub-nav) state.
    const narratives = ref<NarrativeSummary[]>([]);
    const loadedMatterId = ref<string | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Currently-open narrative (detail page) state.
    const current = ref<Narrative | null>(null);
    const loadingCurrent = ref(false);
    const currentError = ref<string | null>(null);

    function matterId(): string {
        const matterStore = useMatterStore();
        const id = matterStore.currentMatter?.RecordId;
        if (!id) throw new Error('No matter selected');
        return id;
    }

    async function parseError(response: Response, fallback: string): Promise<string> {
        const body = await response.json().catch(() => ({}));
        return body?.detail ?? `${fallback} (${response.status})`;
    }

    /** Load the matter's narrative list. Skips the fetch on a same-matter revisit. */
    async function fetchNarratives(force = false): Promise<void> {
        const id = matterId();
        if (!force && loadedMatterId.value === id) return;
        loading.value = true;
        error.value = null;
        try {
            const response = await fetch(`/api/Matter/${id}/Narratives`);
            if (!response.ok) {
                throw new Error(await parseError(response, 'Failed to load narratives'));
            }
            narratives.value = (await response.json()) as NarrativeSummary[];
            loadedMatterId.value = id;
        } catch (e) {
            error.value = e instanceof Error ? e.message : String(e);
            narratives.value = [];
            throw e;
        } finally {
            loading.value = false;
        }
    }

    /** Create a narrative (seeded with one page) and prepend it to the list. */
    async function createNarrative(title?: string): Promise<Narrative> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: title ?? null }),
        });
        if (!response.ok) {
            const message = await parseError(response, 'Failed to create narrative');
            error.value = message;
            throw new Error(message);
        }
        const narrative = (await response.json()) as Narrative;
        narratives.value.unshift(toSummary(narrative));
        captureEvent('narrative_created', { matter_id: id, narrative_id: narrative.id });
        return narrative;
    }

    /** Fetch a single narrative (with its pages) into `current`. */
    async function fetchNarrative(narrativeId: string): Promise<void> {
        const id = matterId();
        loadingCurrent.value = true;
        currentError.value = null;
        try {
            const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}`);
            if (!response.ok) {
                throw new Error(await parseError(response, 'Failed to load narrative'));
            }
            current.value = (await response.json()) as Narrative;
        } catch (e) {
            currentError.value = e instanceof Error ? e.message : String(e);
            current.value = null;
            throw e;
        } finally {
            loadingCurrent.value = false;
        }
    }

    async function renameNarrative(narrativeId: string, title: string): Promise<void> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title }),
        });
        if (!response.ok) {
            throw new Error(await parseError(response, 'Failed to rename narrative'));
        }
        const updated = (await response.json()) as Narrative;
        if (current.value?.id === narrativeId) current.value.title = updated.title;
        const summary = narratives.value.find((n) => n.id === narrativeId);
        if (summary) summary.title = updated.title;
        captureEvent('narrative_renamed', { matter_id: id, narrative_id: narrativeId });
    }

    async function deleteNarrative(narrativeId: string): Promise<void> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            throw new Error(await parseError(response, 'Failed to delete narrative'));
        }
        narratives.value = narratives.value.filter((n) => n.id !== narrativeId);
        if (current.value?.id === narrativeId) current.value = null;
        captureEvent('narrative_deleted', { matter_id: id, narrative_id: narrativeId });
    }

    /** Add a page to the open narrative (optionally nested under `parentId`). */
    async function createPage(
        narrativeId: string,
        parentId: string | null = null,
        title?: string,
    ): Promise<NarrativePage> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}/Page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ parent_id: parentId, title: title ?? null }),
        });
        if (!response.ok) {
            throw new Error(await parseError(response, 'Failed to add page'));
        }
        const page = (await response.json()) as NarrativePage;
        if (current.value?.id === narrativeId) current.value.pages.push(page);
        bumpPageCount(narrativeId, 1);
        captureEvent('narrative_page_created', {
            matter_id: id,
            narrative_id: narrativeId,
            page_id: page.id,
            is_nested: parentId !== null,
        });
        return page;
    }

    async function renamePage(narrativeId: string, pageId: string, title: string): Promise<void> {
        await patchPage(narrativeId, pageId, { title });
        captureEvent('narrative_page_renamed', {
            matter_id: matterId(),
            narrative_id: narrativeId,
            page_id: pageId,
        });
    }

    /** Persist a page's Editor.js content blob. */
    async function savePageContent(
        narrativeId: string,
        pageId: string,
        content: OutputData,
    ): Promise<void> {
        await patchPage(narrativeId, pageId, { content });
    }

    async function patchPage(
        narrativeId: string,
        pageId: string,
        payload: { title?: string; content?: OutputData },
    ): Promise<void> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}/Page/${pageId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            throw new Error(await parseError(response, 'Failed to update page'));
        }
        const updated = (await response.json()) as NarrativePage;
        if (current.value?.id === narrativeId) {
            const idx = current.value.pages.findIndex((p) => p.id === pageId);
            if (idx !== -1) current.value.pages[idx] = updated;
            // A content save re-derives the page's finding links server-side;
            // refresh the narrative-level rollup so the findings panel's
            // "cited" scope stays in sync without a full refetch.
            recomputeCitedFindings();
        }
    }

    async function deletePage(narrativeId: string, pageId: string): Promise<void> {
        const id = matterId();
        const response = await fetch(`/api/Matter/${id}/Narrative/${narrativeId}/Page/${pageId}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            throw new Error(await parseError(response, 'Failed to delete page'));
        }
        // The server removes descendants too; refetch to keep the tree accurate.
        await fetchNarrative(narrativeId);
        const summary = narratives.value.find((n) => n.id === narrativeId);
        if (summary && current.value?.id === narrativeId) {
            summary.page_count = current.value.pages.length;
        }
        captureEvent('narrative_page_deleted', {
            matter_id: id,
            narrative_id: narrativeId,
            page_id: pageId,
        });
    }

    function toSummary(n: Narrative): NarrativeSummary {
        return {
            id: n.id,
            title: n.title,
            created_on: n.created_on,
            updated_on: n.updated_on,
            page_count: n.pages.length,
            cited_finding_ids: [...n.cited_finding_ids],
        };
    }

    /** Recompute the open narrative's cited-findings rollup from its pages. */
    function recomputeCitedFindings() {
        if (!current.value) return;
        const seen = new Set<string>();
        for (const page of current.value.pages) {
            for (const fid of page.cited_finding_ids ?? []) seen.add(fid);
        }
        current.value.cited_finding_ids = [...seen];
    }

    function bumpPageCount(narrativeId: string, delta: number) {
        const summary = narratives.value.find((n) => n.id === narrativeId);
        if (summary) summary.page_count += delta;
    }

    function reset() {
        narratives.value = [];
        loadedMatterId.value = null;
        loading.value = false;
        error.value = null;
        current.value = null;
        loadingCurrent.value = false;
        currentError.value = null;
    }

    return {
        narratives,
        loadedMatterId,
        loading,
        error,
        current,
        loadingCurrent,
        currentError,
        fetchNarratives,
        createNarrative,
        fetchNarrative,
        renameNarrative,
        deleteNarrative,
        createPage,
        renamePage,
        savePageContent,
        deletePage,
        reset,
    };
});
